import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { trimEndTrailingSlashes } from "../common/utils";
import strings, { defaultLang, languagesList } from "../i18n/strings";
import { IAppStrings } from "../i18n/stringsDef";
import { deStrings } from "../i18n/stringsDE";
import { enStrings } from "../i18n/stringsEN";
import { trStrings } from "../i18n/stringsTR";
import {
  getPersistedLanguageFromCookie,
  useLanguageStore,
} from "../common/languageStore";

export interface ILocalizeContext {
  language: string;
  strings: IAppStrings;
  setLanguage: (lng: string) => void;
  localizePath: (path: string) => string;
}

export const LocalizeContext = React.createContext<ILocalizeContext>({
  language: defaultLang,
  strings: {} as IAppStrings,
  setLanguage: () => undefined,
  localizePath: (path: string) => path,
});

const isValidLanguage = (language?: string): language is string => {
  if (!language) {
    return false;
  }

  return languagesList.some((item) => item.key === language);
};

const getUrlLanguage = (pathname: string): string | undefined => {
  return pathname.split("/").filter(Boolean)[0];
};

export const LocalizeProvider = (props: React.PropsWithChildren) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const storedLanguage = useLanguageStore((state) => state.language);
  const setLanguageInStore = useLanguageStore((state) => state.setLanguage);
  const urlLanguage = getUrlLanguage(pathname);
  const language = isValidLanguage(urlLanguage) ? urlLanguage : storedLanguage;

  const buildLocalizedPath = React.useCallback(
    (nextLanguage: string): string => {
      const pathSegments = pathname.split("/").filter(Boolean);
      const restSegments = isValidLanguage(pathSegments[0])
        ? pathSegments.slice(1)
        : pathSegments;
      const suffix =
        restSegments.length > 0 ? `/${restSegments.join("/")}` : "";

      return trimEndTrailingSlashes(`/${nextLanguage}${suffix}`) ?? "/";
    },
    [pathname]
  );

  // Keep store in sync with URL language and normalize invalid URL language.
  React.useEffect(() => {
    if (urlLanguage && !isValidLanguage(urlLanguage)) {
      setLanguageInStore(defaultLang);
      navigate("/" + defaultLang, { replace: true });
      return;
    }

    const currentLanguage = useLanguageStore.getState().language;

    if (isValidLanguage(urlLanguage) && urlLanguage !== currentLanguage) {
      setLanguageInStore(urlLanguage);
    }
  }, [navigate, setLanguageInStore, urlLanguage]);

  // Keep localization strings aligned with persisted language.
  React.useEffect(() => {
    strings.setLanguage(language);
  }, [language]);

  React.useEffect(() => {
    const syncFromCookie = () => {
      const cookieLanguage = getPersistedLanguageFromCookie();

      if (!cookieLanguage || cookieLanguage === language) {
        return;
      }

      setLanguageInStore(cookieLanguage);
      navigate(buildLocalizedPath(cookieLanguage), { replace: true });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        syncFromCookie();
      }
    };

    window.addEventListener("focus", syncFromCookie);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", syncFromCookie);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [buildLocalizedPath, language, navigate, setLanguageInStore]);

  const context = {
    language,
    strings: ({ de: deStrings, en: enStrings, tr: trStrings } as Record<string, IAppStrings>)[language] || enStrings,
    setLanguage: (lng: string) => {
      if (language === lng) {
        return;
      }

      if (!isValidLanguage(lng)) {
        return;
      }

      setLanguageInStore(lng);
      navigate(buildLocalizedPath(lng), { replace: true });
    },
    localizePath: (path: string) => {
      return trimEndTrailingSlashes(`/${language}${path}`);
    },
  } as ILocalizeContext;

  return (
    <LocalizeContext.Provider value={context}>
      {props.children}
    </LocalizeContext.Provider>
  );
};

export const useLocalize = () => React.useContext(LocalizeContext);
